import type {
  CreateNoteInput,
  Note,
  NoteListQuery,
  NoteSummary,
  NoteTagCount,
  UpdateNoteInput,
} from '@pm/schemas';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { qs } from './queries';

export const noteKeys = {
  all: ['notes'] as const,
  list: (q: NoteListQuery) => ['notes', 'list', q] as const,
  tags: ['notes', 'tags'] as const,
  one: (id: string) => ['notes', 'one', id] as const,
};

export function useNotes(q: NoteListQuery) {
  return useQuery({
    queryKey: noteKeys.list(q),
    queryFn: () => api.get<NoteSummary[]>(`/me/notes${qs({ ...q })}`),
    placeholderData: keepPreviousData,
  });
}

export function useNoteTags() {
  return useQuery({
    queryKey: noteKeys.tags,
    queryFn: () => api.get<NoteTagCount[]>('/me/notes/tags'),
  });
}

export function useNote(id: string | undefined) {
  return useQuery({
    queryKey: noteKeys.one(id ?? ''),
    queryFn: () => api.get<Note>(`/me/notes/${id}`),
    enabled: Boolean(id),
  });
}

/** Salva (cria sem id, altera com id) e já deixa a nota no cache para a tela de leitura. */
export function useSaveNote() {
  const qc = useQueryClient();
  return useMutation<Note, Error, { id?: string; body: CreateNoteInput | UpdateNoteInput }>({
    mutationFn: ({ id, body }) =>
      id ? api.patch<Note>(`/me/notes/${id}`, body) : api.post<Note>('/me/notes', body),
    onSuccess: (note) => {
      qc.setQueryData(noteKeys.one(note.id), note);
      void qc.invalidateQueries({ queryKey: noteKeys.all });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/me/notes/${id}`),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: noteKeys.one(id) });
      void qc.invalidateQueries({ queryKey: noteKeys.all });
    },
  });
}
